import React, { useState, useEffect, useCallback } from 'react';
import {
  collection, query, where, orderBy,
  limit, startAfter, getDocs
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { TRANSACTION_TYPES } from '../utils/stockUtils';
import printTable from '../utils/printUtils';

const PAGE_SIZE = 30;

function useIsMobile() {
  const [v, setV] = useState(() => window.innerWidth < 640);
  useEffect(() => {
    const h = () => setV(window.innerWidth < 640);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return v;
}

export default function ProductHistory({ product, onClose }) {
  const { orgId } = useAuth();
  const isMobile = useIsMobile();
  const [logs, setLogs]               = useState([]);
  const [lastDoc, setLastDoc]         = useState(null);
  const [hasMore, setHasMore]         = useState(false);
  const [loading, setLoading]         = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const buildQuery = useCallback((cursor = null) => {
    const constraints = [
      where('orgId', '==', orgId),
      where('productId', '==', product.id),
      orderBy('timestamp', 'desc'),
      limit(PAGE_SIZE),
    ];
    if (cursor) constraints.push(startAfter(cursor));
    return query(collection(db, 'activityLog'), ...constraints);
  }, [product.id, orgId]);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const snap = await getDocs(buildQuery());
        setLogs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLastDoc(snap.docs[snap.docs.length - 1] || null);
        setHasMore(snap.docs.length === PAGE_SIZE);
      } catch (err) {
        console.error('Failed to fetch product history:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [buildQuery]);

  const fetchMore = async () => {
    if (!lastDoc || loadingMore) return;
    setLoadingMore(true);
    try {
      const snap = await getDocs(buildQuery(lastDoc));
      setLogs(prev => [...prev, ...snap.docs.map(d => ({ id: d.id, ...d.data() }))]);
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.docs.length === PAGE_SIZE);
    } catch (err) {
      console.error('Failed to fetch more:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const totalIn  = logs.filter(l => l.delta > 0).reduce((s, l) => s + l.delta, 0);
  const totalOut = logs.filter(l => l.delta < 0).reduce((s, l) => s + l.delta, 0);

  const printHistory = () => {
    printTable({
      title: `Stock History — ${product.name}`,
      subtitle: `Current stock: ${product.stock}${product.minStock != null ? ` · Min: ${product.minStock}` : ''} · MOQ: ${product.moq} · ${logs.length} transactions`,
      columns: ['Time', 'Type', 'Change', 'Stock After', 'User', 'Notes'],
      rows: logs.map(log => {
        const txn = TRANSACTION_TYPES[log.transactionType];
        return [
          log.timestamp?.toDate().toLocaleString() || '—',
          txn?.label || log.transactionType,
          {
            value: log.delta > 0 ? `+${log.delta}` : log.delta,
            style: `color:${log.delta > 0 ? '#16a34a' : log.delta < 0 ? '#ef4444' : '#64748b'};font-weight:700;`,
          },
          { value: log.newStock, style: 'font-weight:600;' },
          log.userName || '—',
          log.notes || '—',
        ];
      }),
      styles: { colWidths: ['20%', '16%', '12%', '14%', '16%', '22%'] },
    });
  };

  // ── Summary strip (shared) ───────────────────────────────────────────────────
  const SummaryStrip = () => (
    <div style={isMobile ? m.summaryStrip : styles.summaryStrip}>
      {[
        { label: 'Total In',     value: `+${totalIn}`,                      color: '#16a34a', bg: '#f0fdf4' },
        { label: 'Total Out',    value: totalOut,                            color: '#ef4444', bg: '#fef2f2' },
        { label: 'Transactions', value: logs.length + (hasMore ? '+' : ''), color: '#3b82f6', bg: '#eff6ff' },
      ].map(card => (
        <div key={card.label} style={{
          ...(isMobile ? m.summaryCard : styles.summaryCard),
          backgroundColor: card.bg,
        }}>
          <span style={{
            ...(isMobile ? m.summaryValue : styles.summaryValue),
            color: card.color,
          }}>{card.value}</span>
          <span style={isMobile ? m.summaryLabel : styles.summaryLabel}>{card.label}</span>
        </div>
      ))}
    </div>
  );

  // ── Mobile log card ──────────────────────────────────────────────────────────
  const MobileLogCard = ({ log }) => {
    const txn   = TRANSACTION_TYPES[log.transactionType];
    const delta = log.delta;
    return (
      <div style={m.card}>
        <div style={m.cardTopRow}>
          <span style={{
            ...m.typeBadge,
            backgroundColor: txn?.bg || '#f1f5f9',
            color: txn?.color || '#64748b',
            border: `1px solid ${(txn?.color || '#e2e8f0')}44`,
          }}>
            {txn?.label || log.transactionType}
          </span>
          <span style={{
            ...m.delta,
            color: delta > 0 ? '#16a34a' : delta < 0 ? '#ef4444' : '#64748b',
          }}>
            {delta > 0 ? `+${delta}` : delta}
          </span>
        </div>

        <div style={m.metaRow}>
          <span style={m.stockFlow}>{log.oldStock} → {log.newStock}</span>
          <span style={m.metaText}>👤 {log.userName}</span>
        </div>

        {log.notes && (
          <div style={m.notesBox}>
            <span style={m.notesIcon}>📝</span> {log.notes}
          </div>
        )}

        <div style={m.timestamp}>
          {log.timestamp?.toDate().toLocaleString() || '—'}
        </div>
      </div>
    );
  };

  // ── MOBILE LAYOUT ────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div style={m.overlay} onClick={onClose}>
        <div style={m.sheet} onClick={e => e.stopPropagation()}>

          <div style={m.dragHandle} />

          <div style={m.header}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2 style={m.title}>{product.name}</h2>
              <p style={m.subtitle}>
                Stock: <strong>{product.stock}</strong>
                {product.minStock != null && ` · Min: ${product.minStock}`}
                {` · MOQ: ${product.moq}`}
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
              <button onClick={printHistory} style={m.headerBtn}>🖨️ Print</button>
              <button onClick={onClose} style={m.closeBtn}>✕</button>
            </div>
          </div>

          <SummaryStrip />

          <div style={m.scrollArea}>
            {loading ? (
              <div style={m.emptyState}>Loading history…</div>
            ) : logs.length === 0 ? (
              <div style={m.emptyState}>No transactions recorded yet</div>
            ) : (
              <>
                {logs.map(log => <MobileLogCard key={log.id} log={log} />)}

                {hasMore && (
                  <div style={{ padding: '8px 0 4px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                    <button onClick={fetchMore} disabled={loadingMore}
                      style={loadingMore ? m.loadMoreDisabled : m.loadMore}>
                      {loadingMore ? 'Loading…' : `Load ${PAGE_SIZE} more`}
                    </button>
                    <span style={{ fontSize: '12px', color: '#94a3b8' }}>{logs.length} loaded</span>
                  </div>
                )}

                {!hasMore && (
                  <p style={{ textAlign: 'center', fontSize: '12px', color: '#cbd5e1', padding: '12px 0 4px' }}>
                    All {logs.length} entries loaded
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── DESKTOP LAYOUT ───────────────────────────────────────────────────────────
  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.drawer} onClick={e => e.stopPropagation()}>

        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>{product.name}</h2>
            <p style={styles.subtitle}>
              Stock history · Current stock: <strong>{product.stock}</strong>
              {product.minStock != null && ` · Min: ${product.minStock}`}
              {` · MOQ: ${product.moq}`}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
            <button onClick={printHistory} style={styles.printBtn}>🖨️ Print</button>
            <button onClick={onClose} style={styles.closeBtn}>✕</button>
          </div>
        </div>

        <SummaryStrip />

        <div style={styles.tableWrapper}>
          {loading ? (
            <div style={styles.loadingState}>Loading history...</div>
          ) : logs.length === 0 ? (
            <div style={styles.loadingState}>No transactions recorded yet</div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  {['Time', 'Type', 'Change', 'Stock After', 'User', 'Notes'].map(h => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map(log => {
                  const txn = TRANSACTION_TYPES[log.transactionType];
                  return (
                    <tr key={log.id} style={styles.tr}>
                      <td style={{ ...styles.td, color: '#94a3b8', fontSize: '12px', whiteSpace: 'nowrap' }}>
                        {log.timestamp?.toDate().toLocaleString() || '—'}
                      </td>
                      <td style={styles.td}>
                        <span style={{
                          backgroundColor: txn?.bg || '#f1f5f9',
                          color: txn?.color || '#64748b',
                          padding: '3px 10px', borderRadius: '20px',
                          fontSize: '12px', fontWeight: '600',
                          border: `1px solid ${txn?.color || '#e2e8f0'}44`,
                        }}>
                          {txn?.label || log.transactionType}
                        </span>
                      </td>
                      <td style={{
                        ...styles.td, fontWeight: '700', fontSize: '15px',
                        color: log.delta > 0 ? '#16a34a' : log.delta < 0 ? '#ef4444' : '#64748b',
                      }}>
                        {log.delta > 0 ? `+${log.delta}` : log.delta}
                      </td>
                      <td style={{ ...styles.td, fontWeight: '600', color: '#1e293b' }}>
                        {log.newStock}
                      </td>
                      <td style={{ ...styles.td, color: '#64748b', fontSize: '13px' }}>
                        {log.userName}
                      </td>
                      <td style={{ ...styles.td, color: '#64748b', fontSize: '13px', maxWidth: '180px' }}>
                        {log.notes
                          ? <span title={log.notes} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📝 {log.notes}</span>
                          : <span style={{ color: '#cbd5e1' }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {hasMore && !loading && (
          <div style={styles.loadMoreRow}>
            <button onClick={fetchMore} disabled={loadingMore}
              style={loadingMore ? styles.loadMoreBtnDisabled : styles.loadMoreBtn}>
              {loadingMore ? 'Loading...' : `Load ${PAGE_SIZE} more`}
            </button>
            <span style={styles.loadMoreHint}>{logs.length} entries loaded</span>
          </div>
        )}

        {!hasMore && logs.length > 0 && !loading && (
          <p style={styles.allLoadedHint}>All {logs.length} entries loaded</p>
        )}
      </div>
    </div>
  );
}

// ── Mobile styles ─────────────────────────────────────────────────────────────
const m = {
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.5)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', zIndex: 1000, backdropFilter: 'blur(2px)' },
  sheet: { backgroundColor: '#ffffff', width: '100%', maxHeight: '92dvh', borderRadius: '20px 20px 0 0', display: 'flex', flexDirection: 'column', boxShadow: '0 -4px 32px rgba(0,0,0,0.18)', overflow: 'hidden' },
  dragHandle: { width: '40px', height: '4px', borderRadius: '2px', backgroundColor: '#e2e8f0', margin: '12px auto 4px', flexShrink: 0 },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '12px 16px 14px', borderBottom: '1px solid #f1f5f9', flexShrink: 0, gap: '12px' },
  title: { fontSize: '17px', fontWeight: '700', color: '#1e293b', marginBottom: '3px' },
  subtitle: { fontSize: '12px', color: '#64748b', lineHeight: 1.5 },
  headerBtn: { padding: '5px 12px', backgroundColor: '#ffffff', border: '1.5px solid #e2e8f0', color: '#475569', borderRadius: '8px', fontSize: '12px', cursor: 'pointer' },
  closeBtn: { background: 'none', border: 'none', color: '#94a3b8', fontSize: '18px', cursor: 'pointer', padding: '2px' },
  summaryStrip: { display: 'flex', gap: '8px', padding: '12px 16px', borderBottom: '1px solid #f1f5f9', flexShrink: 0 },
  summaryCard: { flex: 1, borderRadius: '10px', padding: '10px', display: 'flex', flexDirection: 'column', gap: '2px' },
  summaryValue: { fontSize: '18px', fontWeight: '800' },
  summaryLabel: { fontSize: '10px', color: '#64748b', fontWeight: '500' },
  scrollArea: { flex: 1, overflowY: 'auto', padding: '12px 16px', WebkitOverflowScrolling: 'touch' },
  emptyState: { padding: '60px 0', textAlign: 'center', color: '#94a3b8', fontSize: '14px' },
  card: { backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px 14px', marginBottom: '8px' },
  cardTopRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' },
  typeBadge: { padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' },
  delta: { fontSize: '18px', fontWeight: '800' },
  metaRow: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' },
  stockFlow: { fontSize: '12px', color: '#64748b', backgroundColor: '#ffffff', border: '1px solid #e2e8f0', padding: '2px 8px', borderRadius: '6px' },
  metaText: { fontSize: '12px', color: '#64748b' },
  notesBox: { fontSize: '12px', color: '#475569', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', padding: '6px 9px', marginBottom: '6px', lineHeight: 1.4 },
  notesIcon: { marginRight: '4px' },
  timestamp: { fontSize: '11px', color: '#94a3b8' },
  loadMore: { width: '100%', padding: '12px', backgroundColor: '#ffffff', border: '1.5px solid #e2e8f0', color: '#475569', borderRadius: '10px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  loadMoreDisabled: { width: '100%', padding: '12px', backgroundColor: '#f8fafc', border: '1.5px solid #e2e8f0', color: '#94a3b8', borderRadius: '10px', fontSize: '14px', cursor: 'not-allowed' },
};

// ── Desktop styles ────────────────────────────────────────────────────────────
const styles = {
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.4)', display: 'flex', justifyContent: 'flex-end', zIndex: 1000, backdropFilter: 'blur(2px)' },
  drawer: { backgroundColor: '#ffffff', width: '100%', maxWidth: '700px', height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '-8px 0 40px rgba(0,0,0,0.15)', overflow: 'hidden' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '24px 24px 16px', borderBottom: '1px solid #e2e8f0', flexShrink: 0 },
  title: { fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' },
  subtitle: { fontSize: '13px', color: '#64748b' },
  printBtn: { padding: '6px 14px', backgroundColor: '#ffffff', border: '1.5px solid #e2e8f0', color: '#475569', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' },
  closeBtn: { background: 'none', border: 'none', color: '#94a3b8', fontSize: '20px', cursor: 'pointer', padding: '4px' },
  summaryStrip: { display: 'flex', gap: '12px', padding: '16px 24px', borderBottom: '1px solid #f1f5f9', flexShrink: 0 },
  summaryCard: { flex: 1, borderRadius: '8px', padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: '2px' },
  summaryValue: { fontSize: '20px', fontWeight: '800' },
  summaryLabel: { fontSize: '11px', color: '#64748b', fontWeight: '500' },
  tableWrapper: { flex: 1, overflowY: 'auto' },
  loadingState: { padding: '60px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '11px 16px', textAlign: 'left', color: '#64748b', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', backgroundColor: '#f8fafc', whiteSpace: 'nowrap', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0 },
  tr: { borderBottom: '1px solid #f1f5f9' },
  td: { padding: '12px 16px', color: '#374151', fontSize: '14px' },
  loadMoreRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '16px 24px', borderTop: '1px solid #f1f5f9', flexShrink: 0 },
  loadMoreBtn: { padding: '8px 20px', backgroundColor: '#ffffff', border: '1.5px solid #e2e8f0', color: '#475569', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' },
  loadMoreBtnDisabled: { padding: '8px 20px', backgroundColor: '#f8fafc', border: '1.5px solid #e2e8f0', color: '#94a3b8', borderRadius: '8px', fontSize: '13px', cursor: 'not-allowed' },
  loadMoreHint: { fontSize: '12px', color: '#94a3b8' },
  allLoadedHint: { textAlign: 'center', padding: '12px', fontSize: '12px', color: '#cbd5e1', flexShrink: 0 },
};